import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Accessibility, ShieldCheck, X } from 'lucide-react';

const peso = (n) => `\u20b1${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Captures the Senior Citizen / PWD cardholder details required by law
 * (Name + OSCA/PWD ID), an optional manager PIN, and shows the live
 * VAT-removal + statutory-discount breakdown before applying.
 */
export default function ScpwdDiscountModal({
  open, onClose, onApply, requirePin = false, taxConfig = {}, preview = null, initial = null,
}) {
  const [type, setType] = useState('senior');
  const [name, setName] = useState('');
  const [cardId, setCardId] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setType(initial?.type || 'senior');
      setName(initial?.card_name || '');
      setCardId(initial?.card_id || '');
      setPin('');
      setErr('');
    }
  }, [open, initial]);

  const scpwdPct = taxConfig?.scpwd_discount_percent ?? 20;

  const submit = () => {
    if (!name.trim() || !cardId.trim()) {
      setErr('Cardholder Name and OSCA / PWD ID are both required by law.');
      return;
    }
    if (requirePin && !pin.trim()) {
      setErr('Manager PIN is required to authorize this discount.');
      return;
    }
    onApply({ enabled: true, type, card_name: name.trim(), card_id: cardId.trim(), pin: pin.trim() || undefined });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="scpwd-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Accessibility size={18} /> Senior Citizen / PWD Discount
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            VAT is removed from vatable items, then a <span className="font-semibold text-[#1A4D2E]">{scpwdPct}%</span> discount
            is applied to the VAT-net amount (RA 9994 / RA 10754). Use a <span className="font-semibold">separate</span> sale
            for non-eligible companions.
          </p>

          {/* Type selector */}
          <div>
            <Label className="text-xs text-slate-600">Cardholder Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {[{ v: 'senior', l: 'Senior Citizen' }, { v: 'pwd', l: 'PWD' }].map(o => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setType(o.v)}
                  data-testid={`scpwd-type-${o.v}`}
                  className={`h-10 rounded-lg border-2 text-sm font-medium transition-all ${type === o.v ? 'border-[#1A4D2E] bg-emerald-50 text-[#1A4D2E]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Cardholder Name *</Label>
            <Input
              data-testid="scpwd-name-input"
              className="mt-1 h-10"
              placeholder="Full name as on the ID"
              value={name}
              onChange={e => setName(e.target.value)} />
          </div>

          <div>
            <Label className="text-xs text-slate-600">{type === 'pwd' ? 'PWD ID Number *' : 'OSCA / Senior Citizen ID *'}</Label>
            <Input
              data-testid="scpwd-id-input"
              className="mt-1 h-10"
              placeholder={type === 'pwd' ? 'PWD ID No.' : 'OSCA / SC ID No.'}
              value={cardId}
              onChange={e => setCardId(e.target.value)} />
          </div>

          {requirePin && (
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1.5"><ShieldCheck size={13} /> Manager PIN *</Label>
              <Input
                data-testid="scpwd-pin-input"
                type="password"
                inputMode="numeric"
                className="mt-1 h-10 font-mono tracking-widest"
                placeholder="••••"
                value={pin}
                onChange={e => setPin(e.target.value)} />
            </div>
          )}

          {/* Live breakdown */}
          {preview && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm space-y-1" data-testid="scpwd-preview">
              <div className="flex justify-between text-slate-600"><span>Gross Sales</span><span className="font-mono">{peso(preview.gross)}</span></div>
              {preview.vatAmount > 0 && (
                <div className="flex justify-between text-slate-600"><span>Less: VAT</span><span className="font-mono">- {peso(preview.vatAmount)}</span></div>
              )}
              <div className="flex justify-between text-slate-600"><span>VAT-Exempt Sales</span><span className="font-mono">{peso(preview.vatExempt)}</span></div>
              <div className="flex justify-between text-[#1A4D2E] font-medium"><span>Less: {scpwdPct}% SC/PWD Discount</span><span className="font-mono">- {peso(preview.discount)}</span></div>
              <div className="flex justify-between pt-1 border-t border-emerald-200 font-bold text-[#1A4D2E]"><span>Amount Due</span><span className="font-mono" data-testid="scpwd-net-due">{peso(preview.netDue)}</span></div>
            </div>
          )}

          {err && <p className="text-xs text-red-600" data-testid="scpwd-error">{err}</p>}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="scpwd-cancel-btn">
              <X size={15} className="mr-1" /> Cancel
            </Button>
            <Button className="flex-1 bg-[#1A4D2E] hover:bg-[#143d24]" onClick={submit} data-testid="scpwd-apply-btn">
              Apply Discount
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
